import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getIndustryBySlug, Industry } from "@/data/industries";
import { IndustryLayout } from "./industries/Layout";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import FreeAuditForm from "@/components/site/FreeAuditForm";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const IndustryDetail = () => {
  const { slug } = useParams<{ slug: Industry["slug"] }>();
  const industry = slug ? getIndustryBySlug(slug) : undefined;
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);

  const title = industry ? `AI for ${industry.title} in Montclair, NJ` : "Industry";
  const description = industry?.benefit || "Discover how AI automation can optimize your operations.";
  const canonical = useMemo(() => (typeof window !== "undefined" ? `${window.location.origin}/industries/${slug}` : undefined), [slug]);

  const jsonLd = useMemo<Record<string, unknown>[] | undefined>(() => {
    if (!industry || !canonical) return undefined;
    const org = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "AI Montclair",
      url: canonical.replace(/\/industries\/.+$/, "/"),
      logo: canonical.replace(/\/industries\/.+$/, "/public/favicon.ico"),
    };

    const local = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "AI Montclair",
      url: canonical.replace(/\/industries\/.+$/, "/"),
      areaServed: "Montclair, NJ",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Montclair",
        addressRegion: "NJ",
        addressCountry: "US",
      },
      image: industry.img,
    };

    const faq = industry.faq.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: industry.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : undefined;

    return faq ? [org, local, faq] : [org, local];
  }, [industry, canonical]);

  useEffect(() => {
    // ensure scroll to top when navigating between slugs
    window.scrollTo(0, 0);
  }, [slug]);

  if (!industry) {
    return (
      <main className="container mx-auto py-16">
        <h1 className="text-3xl font-semibold">Industry not found</h1>
        <p className="mt-2 text-muted-foreground">The page you’re looking for doesn’t exist.</p>
        <Button asChild className="mt-6">
          <Link to="/">Go back home</Link>
        </Button>
      </main>
    );
  }

  return (
    <>
      <Navbar />
      <Seo
        title={`${title} | AI Montclair`}
        description={description}
        canonical={canonical}
        image={industry.img}
        ogType="website"
        jsonLd={jsonLd}
      />

      <IndustryLayout title={title} subtitle={industry.benefit}>
        <section className="grid gap-8 md:grid-cols-2">
          <img
            src={industry.img}
            alt={industry.alt}
            loading="lazy"
            className="w-full h-64 md:h-80 object-cover rounded-md"
          />
          <article>
            <h2 className="text-xl font-semibold">What we automate</h2>
            <ul className="mt-4 list-disc pl-5 space-y-2 text-muted-foreground">
              {industry.automate.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        </section>

        <section className="mt-12 grid gap-6 md:grid-cols-2">
          <article>
            <h2 className="text-xl font-semibold">Quick wins & ROI</h2>
            <ul className="mt-4 list-disc pl-5 space-y-2 text-muted-foreground">
              {industry.quickWins.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
            <p className="mt-3 text-base text-muted-foreground">{industry.roi}</p>
          </article>
          <article>
            <h2 className="text-xl font-semibold">How it works</h2>
            <ol className="mt-4 list-decimal pl-5 space-y-2 text-muted-foreground">
              {industry.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </article>
        </section>

        {industry.faq.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-semibold">FAQ</h2>
            <div className="mt-4 space-y-4">
              {industry.faq.map((f) => (
                <details key={f.q} className="rounded-md border p-4">
                  <summary className="font-medium cursor-pointer">{f.q}</summary>
                  <p className="mt-2 text-base text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12">
          <div className="rounded-md bg-[hsl(var(--sidebar-background))] p-6 md:p-8">
            <h2 className="text-xl font-semibold">Ready to get started?</h2>
            <p className="mt-2 text-muted-foreground">{industry.cta}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
                <DialogTrigger asChild>
                  <Button>Book your free AI audit</Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Free Consultation</DialogTitle>
          <DialogDescription>
            Answer a few quick questions to book your free consultation.
          </DialogDescription>
        </DialogHeader>
                  <FreeAuditForm onSubmitted={() => setAuditDialogOpen(false)} />
                </DialogContent>
              </Dialog>
              <Button variant="outline" asChild>
                <Link to="/">Back to home</Link>
              </Button>
            </div>
          </div>
        </section>
      </IndustryLayout>

      <Footer />
    </>
  );
};

export default IndustryDetail;
